import { Grid, Title, Text, Stack, Box, Button, NumberInput, SegmentedControl, LoadingOverlay, Image, Group, TextInput, em, Checkbox, Textarea, Select, Modal } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAvailabilityAndSettings, selectBookingCreateLoading, selectLoading, selectRedirectUrl, selectFormData, selectReservationContent, selectPromoCodeValidation, selectPromoCodeLoading } from '../../../redux/selectors/publicSelector';
import { createBooking, getAvailability, validatePromoCode, clearPromoCode } from '../../../redux/reducers/publicSlice';
import { AvailabilityDay } from '../../../models/availability.model';
import { ActivePaymentService, CalendarActivatedSeason, CalendarActivatedSeasonsResponse, Setting, SettingKey, TransportationConfig } from '../../../models/setting.model';
import { useForm, isEmail, isNotEmpty } from '@mantine/form';
import { Booking, BookingPaymentStatus, BookingSource, BookingType, CardlinkPaymentFormData } from '../../../models/booking.model';
import { PromoCodeDiscountType } from '../../../models/promocode.model';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { Show } from '../../../models/show.model';
import { checkBoxStyles, datePickerStyles, getInputStyles, getTextareaStyles, numberAdultInputStyles, numberInputStyles, textStyles, selectStyles, transportationSelectStyles } from './reservation.helper';
import { areAthensAndCalendarDatesEqual, fromCalendarDateToAthens, getAthensDate, toAthensShowDateTime } from '../../../utils/helper.utils';
import { DateTime } from 'luxon';
import { Logger } from 'utils/logger';
import { getDeviceInfo } from '../device-info/device-info';
import DialogStatus from '../../common/dialog-status/dialog-status';
import CruiseShipModal from '../cruise-ship-modal/cruise-ship-modal';
import vivaLogo from '/assets/images/viva-logo.png';
import colors from '../../../utils/styles';
import './reservation.page.css';

enum ShowAvailabilityStatus {
    FULL = 'full',
    ALMOST_FULL = 'almostFull',
    LOW = 'low',
}

interface ReservationForm {
    date: Date | null;
    showTime: string | null;
    show_id: string | null;
    ticketType: string;
    adultTicketCount: number;
    childTicketCount: number;
    email: string;
    confirmEmail: string;
    fullName: string;
    phoneNumber: string;
    acceptTerms: boolean;
    notes: string;
    hasTravelInsurance: boolean;
    includeTransportation: boolean;
    transportationDestination: string | null;
}

const ReservationPage = ({ calendarRef }: { calendarRef: React.RefObject<HTMLDivElement> }) => {
    const dispatch = useDispatch();
    const isTablet = useMediaQuery(`(max-width: ${em(990)})`);
    const isMaxLargeTablet = useMediaQuery(`(max-width: ${em(1024)})`);

    const [dateClicked, setDateClicked] = useState<Date | null>(null);
    const [selectedShow, setSelectedShow] = useState<Show | null>(null);

    const [openedCruiseShipPassengersModal, { open: openCruiseShipPassengersModal, close: closeCruiseShipPassengersModal }] = useDisclosure(false);
    const [openedInsuranceModal, { open: openInsuranceModal, close: closeInsuranceModal }] = useDisclosure(false);

    const { availability, settings } = useSelector(selectAvailabilityAndSettings);
    const bookingCreateLoading = useSelector(selectBookingCreateLoading);
    const loading = useSelector(selectLoading);
    const redirectUrl = useSelector(selectRedirectUrl);
    const formData: CardlinkPaymentFormData = useSelector(selectFormData);
    const reservation = useSelector(selectReservationContent);
    const promoCodeValidation = useSelector(selectPromoCodeValidation);
    const promoCodeLoading = useSelector(selectPromoCodeLoading);

    const [promoCodeInput, setPromoCodeInput] = useState('');

    const form = useForm<ReservationForm>({
        validateInputOnChange: true,
        initialValues: {
            date: getAthensDate() as Date,
            showTime: null,
            show_id: null,
            ticketType: BookingType.STANDARD,
            adultTicketCount: 2,
            childTicketCount: 0,
            email: '',
            confirmEmail: '',
            fullName: '',
            phoneNumber: '',
            notes: '',
            acceptTerms: false,
            hasTravelInsurance: false,
            includeTransportation: false,
            transportationDestination: null,
        },
        validate: {
            fullName: isNotEmpty('Name cannot be empty'),
            email: isEmail('Invalid email'),
            confirmEmail: (value, values) =>
                !value ? 'Confirmation email is required' :
                    value !== values.email ? 'Emails must match' : null,
            phoneNumber: (value) => {
                if (!value) return 'Phone number is required';

                // Check if it contains only digits and plus
                const onlyDigitsOrPlus = /^[0-9+]+$/;
                if (!onlyDigitsOrPlus.test(value)) {
                    return 'Phone number must include only digits and plus(+)';
                }

                // Additional check: ensure total length doesn't exceed 15 digits
                if (value.length > 16) { // +15 digits = 16 characters max
                    return 'Phone number is too long (maximum 15 digits including country code)';
                }

                // ITU-T E.164 compliant validation
                // Must start with +, followed by 1-4 digit country code, then national number
                // Total length must be 7-15 digits (including country code)
                const e164Regex = /^\+[1-9]\d{6,14}$/;
                if (!e164Regex.test(value)) {
                    return 'Phone number must start with plus(+) followed by 1-4 digits (country code), then national number';
                }

                return null;
            },
            date: isNotEmpty('Please select a date'),
            transportationDestination: (value, values) =>
                values.includeTransportation && !value ? 'Please select a transportation destination' : null,
            acceptTerms: (value) => value === false ? 'You must accept the cancellation policy' : null,
            notes: (value) => value.length > 100 ? 'Notes cannot be more than 100 characters' : null,
            adultTicketCount: (value) => {
                if (isNaN(value) || value === null || value === undefined) return 'Please enter a valid number';
                if (value < 1) return 'Adult ticket count cannot be less than 1';
                return null;
            },
            childTicketCount: (value) => {
                if (isNaN(value) || value === null || value === undefined) return 'Please enter a valid number';
                if (value < 0) return 'Child ticket count cannot be less than 0';
                return null;
            },
        }
    });

    const {
        isPaymentCustomerDetailsEnabled,
        limitedAvailabilityThreshold,
        fullShowColor,
        almostFullShowColor,
        availableShowColor,
        childTicketPrice,
        standardTicketPrice,
        vipTicketPrice,
        vipTicketChildPrice,
        additionalFeePercentage,
        travelInsurancePrice,
        maintainanceMode,
        calendarActivatedSeasons,
        activePaymentService,
        transportationConfig
    } = useMemo(() => {
        let isPaymentCustomerDetailsEnabled = true;
        let limitedAvailabilityThreshold = 80;
        let fullShowColor = '#ff0000';
        let almostFullShowColor = '#ff6900';
        let availableShowColor = '#40ad26';
        let childTicketPrice = 65;
        let standardTicketPrice = 80;
        let vipTicketPrice = 105;
        let vipTicketChildPrice = 90;
        let additionalFeePercentage = 2.24;
        let travelInsurancePrice = 5;
        let maintainanceMode = false;
        let calendarActivatedSeasons: CalendarActivatedSeason[] = [];
        let activePaymentService = ActivePaymentService.CARDLINK;
        let transportationConfig: TransportationConfig = { enabled: false, globalPrice: 10, destinations: [] };

        settings.forEach((setting: Setting) => {
            if (setting.setting_key === SettingKey.ENABLE_PAYMENT_CUSTOMER_DETAILS) {
                isPaymentCustomerDetailsEnabled = setting.setting_value as boolean;
            }
            if (setting.setting_key === SettingKey.LIMITED_AVAILABILITY_THRESHOLD) {
                limitedAvailabilityThreshold = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.FULL_SHOW_COLOR) {
                fullShowColor = setting.setting_value as string;
            }
            if (setting.setting_key === SettingKey.ALMOST_FULL_SHOW_COLOR) {
                almostFullShowColor = setting.setting_value as string;
            }
            if (setting.setting_key === SettingKey.AVAILABLE_SHOW_COLOR) {
                availableShowColor = setting.setting_value as string;
            }
            if (setting.setting_key === SettingKey.CHILD_TICKET_PRICE) {
                childTicketPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.STANDARD_TICKET_PRICE) {
                standardTicketPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.VIP_TICKET_PRICE) {
                vipTicketPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.VIP_TICKET_CHILD_PRICE) {
                vipTicketChildPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.ADDITIONAL_FEE_PERCENTAGE) {
                additionalFeePercentage = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.TRAVEL_INSURANCE_PRICE) {
                travelInsurancePrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.MAINTENANCE_MODE) {
                maintainanceMode = setting.setting_value as boolean;
            }
            if (setting.setting_key === SettingKey.CALENDAR_ACTIVATED_SEASONS) {
                calendarActivatedSeasons = (setting.setting_value as CalendarActivatedSeasonsResponse).seasons;
            }
            if (setting.setting_key === SettingKey.ACTIVE_PAYMENT_SERVICE) {
                activePaymentService = setting.setting_value as ActivePaymentService;
            }
            if (setting.setting_key === SettingKey.TRANSPORTATION_CONFIG) {
                const value = setting.setting_value as Partial<TransportationConfig>;
                transportationConfig = {
                    enabled: value?.enabled ?? false,
                    globalPrice: value?.globalPrice ?? 10,
                    destinations: value?.destinations ?? [],
                };
            }
        });

        if (selectedShow) {
            if (selectedShow.special_adult_price > 0) {
                standardTicketPrice = selectedShow.special_adult_price;
            }
            if (selectedShow.special_children_price > 0) {
                childTicketPrice = selectedShow.special_children_price;
            }
            if (selectedShow.special_vip_adult_price > 0) {
                vipTicketPrice = selectedShow.special_vip_adult_price;
            }
            if (selectedShow.special_vip_children_price > 0) {
                vipTicketChildPrice = selectedShow.special_vip_children_price;
            }
        }

        return {
            isPaymentCustomerDetailsEnabled,
            limitedAvailabilityThreshold,
            fullShowColor,
            almostFullShowColor,
            availableShowColor,
            childTicketPrice,
            standardTicketPrice,
            vipTicketPrice,
            vipTicketChildPrice,
            additionalFeePercentage,
            travelInsurancePrice,
            maintainanceMode,
            calendarActivatedSeasons,
            activePaymentService,
            transportationConfig
        }
    }, [settings, selectedShow]);

    const getShowAvailabilityByOccupancy = (show: Show) => {
        const occupancyRatio = ((show.max_seats - show.available_total_seats) / show.max_seats) * 100;
        if (occupancyRatio === 1) {
            return ShowAvailabilityStatus.FULL;
        } else if (occupancyRatio < limitedAvailabilityThreshold) {
            return ShowAvailabilityStatus.LOW;
        } else {
            return ShowAvailabilityStatus.ALMOST_FULL;
        }
    }

    const isSelectedShowAlmostFull = useMemo(() => {
        if (!selectedShow) return false;
        return selectedShow.is_almost_full || getShowAvailabilityByOccupancy(selectedShow) === ShowAvailabilityStatus.ALMOST_FULL;
    }, [selectedShow]);

    const showTransportation = transportationConfig.enabled && transportationConfig.destinations.length > 0;

    // Only append the global price to the checkbox title when no destination has its own dedicated price.
    const hasDedicatedDestinationPrice = transportationConfig.destinations.some((destination) => destination.price > 0);
    const transportationCheckboxLabel = `ADD YOUR AFTER-SHOW TRANSFER${hasDedicatedDestinationPrice ? '' : ` = ONLY ${transportationConfig.globalPrice}€ per person`}`;

    const transportationOptions = useMemo(() => {
        return transportationConfig.destinations.map((destination) => {
            // Show the price in the dropdown only when the destination has a dedicated price.
            return {
                value: destination.name,
                label: destination.price > 0 ? `${destination.name} - ${destination.price}€ per person` : destination.name,
            };
        });
    }, [transportationConfig]);

    const selectedTransportationPrice = useMemo(() => {
        if (!form.values.includeTransportation || !form.values.transportationDestination) return 0;
        const destination = transportationConfig.destinations.find((item) => item.name === form.values.transportationDestination);
        if (!destination) return 0;
        return destination.price > 0 ? destination.price : transportationConfig.globalPrice;
    }, [form.values.includeTransportation, form.values.transportationDestination, transportationConfig]);

    const totalPrice = useMemo(() => {
        let total = 0;

        if (form.values.ticketType === BookingType.STANDARD) {
            total = (form.values.adultTicketCount * standardTicketPrice) + (form.values.childTicketCount * childTicketPrice);
        } else {
            total = (form.values.adultTicketCount * vipTicketPrice) + (form.values.childTicketCount * vipTicketChildPrice);
        }
        const additionalFee = (total * additionalFeePercentage) / 100;
        const totalGuests = form.values.adultTicketCount + form.values.childTicketCount;
        const insuranceCost = form.values.hasTravelInsurance
            ? travelInsurancePrice * totalGuests
            : 0;
        const transportationCost = selectedTransportationPrice * totalGuests;
        return total + additionalFee + insuranceCost + transportationCost;
    }, [form.values.ticketType, form.values.adultTicketCount, form.values.childTicketCount, form.values.hasTravelInsurance, standardTicketPrice, childTicketPrice, vipTicketPrice, vipTicketChildPrice, additionalFeePercentage, travelInsurancePrice, selectedTransportationPrice]);

    const transportationTotalCost = useMemo(() => {
        return selectedTransportationPrice * (form.values.adultTicketCount + form.values.childTicketCount);
    }, [selectedTransportationPrice, form.values.adultTicketCount, form.values.childTicketCount]);

    const promoDiscountAmount = useMemo(() => {
        if (!promoCodeValidation?.valid) return 0;
        // Transportation is excluded from promo discounts, so only discount the remaining amount.
        const discountableBase = Math.max(0, totalPrice - transportationTotalCost);
        const discount = promoCodeValidation.discount_type === PromoCodeDiscountType.AMOUNT
            ? promoCodeValidation.discount_value
            : (discountableBase * promoCodeValidation.discount_value) / 100;
        // Never discount more than the discountable (non-transportation) portion.
        return Math.min(discountableBase, Math.max(0, discount));
    }, [promoCodeValidation, totalPrice, transportationTotalCost]);

    const discountedTotalPrice = useMemo(() => {
        return Math.max(0, totalPrice - promoDiscountAmount);
    }, [totalPrice, promoDiscountAmount]);

    const promoDiscountLabel = useMemo(() => {
        if (!promoCodeValidation?.valid) return '';
        return promoCodeValidation.discount_type === PromoCodeDiscountType.AMOUNT
            ? `${promoCodeValidation.discount_value}€`
            : `${promoCodeValidation.discount_value}%`;
    }, [promoCodeValidation]);

    const maxDate = useMemo(() => {
        const athensDate = (form.values.date
            ? fromCalendarDateToAthens(form.values.date, false)
            : getAthensDate(undefined, false)) as DateTime;
        const season = calendarActivatedSeasons.find(s => s.year === athensDate.year);
        if (season) {
            const lastMonth = Math.max(...season.monthsEnabled);
            return new Date(athensDate.year, lastMonth, 0);
        }
        return new Date(athensDate.year, 11, 31);
    }, [form.values.date, calendarActivatedSeasons]);

    const minDate = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const selectedYear = form.values.date?.getFullYear() || currentYear;

        if (selectedYear === currentYear) {
            const athensToday = getAthensDate(undefined, false) as DateTime;
            return new Date(athensToday.year, athensToday.month - 1, athensToday.day);
        } else {
            // If different year, use first enabled month of that season
            const season = calendarActivatedSeasons.find(s => s.year === selectedYear);
            if (season) {
                const firstMonth = Math.min(...season.monthsEnabled);
                return DateTime.fromObject({
                    year: selectedYear,
                    month: firstMonth,
                    day: 1
                }).toJSDate();
            }
            return getAthensDate() as Date;
        }
    }, [form.values.date, calendarActivatedSeasons]);

    const childTicketMax = useMemo(() => {
        return selectedShow?.available_children_seats ?? 5;
    }, [selectedShow]);

    const canPay = useMemo(() => {
        const adults = form.values.adultTicketCount;
        const children = form.values.childTicketCount;
        return adults >= 2 || (adults >= 1 && children >= 1);
    }, [form.values.adultTicketCount, form.values.childTicketCount]);

    const adultTicketMax = useMemo(() => {
        const available = selectedShow?.available_adult_seats;
        const vipCap = 50;
        const fallbackWhenNoShow = 50;

        if (form.values.ticketType === BookingType.VIP) {
            // At most 50 per booking, and never more than remain
            if (available === undefined || available === null) {
                return vipCap;
            }
            return available > vipCap ? vipCap : available;
        }

        // Standard: limited only by remaining seats (can exceed 50 if the venue has more)
        return available ?? fallbackWhenNoShow;
    }, [selectedShow, form.values.ticketType]);


    const getDateAvailability = (date: Date) => {
        const selectedAthensDay = (fromCalendarDateToAthens(date, false) as DateTime).startOf('day').toJSDate();
        const athensToday = (getAthensDate(undefined, false) as DateTime).startOf('day').toJSDate();

        if (selectedAthensDay < athensToday) return null;

        const dayData = availability.find((day: AvailabilityDay) => areAthensAndCalendarDatesEqual(day?.date, date));

        if (!dayData || !dayData?.shows?.length) return null;

        const show = dayData.shows[0]; // Assuming one show per day
        if (show.is_full || show.available_total_seats === 0) {
            return ShowAvailabilityStatus.FULL;
        }
        if (show.is_almost_full) {
            return ShowAvailabilityStatus.ALMOST_FULL;
        }

        return getShowAvailabilityByOccupancy(show);
    };

    const handleApplyPromoCode = () => {
        if (!form.values.date) return;
        const bookingDate = toAthensShowDateTime(form.values.date, form.values.showTime);
        dispatch(validatePromoCode({
            code: promoCodeInput.trim(),
            booking_date: bookingDate,
            booked_adult_seats: form.values.adultTicketCount,
            booked_children_seats: form.values.childTicketCount,
        } as any))
    }

    const handleBooking = async (values: ReservationForm) => {
        if (!values.date) return;
        const bookingDate = toAthensShowDateTime(values.date, values.showTime);

        const booking: Partial<Booking> = {
            customer_name: values.fullName,
            customer_email: values.email,
            customer_phone: values.phoneNumber,
            show_id: values.show_id || '',
            booked_adult_seats: values.adultTicketCount,
            booked_children_seats: values.childTicketCount,
            booked_total_seats: values.adultTicketCount + values.childTicketCount,
            booking_type: values.ticketType as BookingType,
            booking_date: bookingDate.toISOString(),
            created_by: values.email || 'web_user',
            created_source: BookingSource.WEBSITE,
            total_price: Number(totalPrice?.toFixed(2) || 0),
            payment_status: BookingPaymentStatus.PENDING,
            booking_from_public_website: true,
            notes: values.notes,
            has_travel_insurance: values.hasTravelInsurance,
            transportation_enabled: values.includeTransportation && !!values.transportationDestination,
            transportation_destination: values.includeTransportation ? (values.transportationDestination || '') : '',
            transportation_price: selectedTransportationPrice,
            promo_code: promoCodeValidation?.valid ? promoCodeValidation.code : '',
            promo_code_discount: promoCodeValidation?.valid ? promoCodeValidation.discount_value : 0,
            promo_code_discount_type: promoCodeValidation?.valid ? (promoCodeValidation.discount_type as string) : '',
        }
        Logger.info(`Booking PUBLIC attempt - created by User ${values.email} - ${values.fullName}`, {
            booking: booking,
            deviceInfo: await getDeviceInfo(),
            dateClicked: dateClicked?.toISOString() || ''
        });
        dispatch(createBooking(booking as any));
    }

    useEffect(() => {
        if (!form.values.date || !availability?.length) return;
        const show = availability?.find((day: AvailabilityDay) => areAthensAndCalendarDatesEqual(day?.date, form.values.date))?.shows?.[0];
        if (show) {
            form.setFieldValue('show_id', show?.id || '');
            form.setFieldValue('showTime', show?.show_time || '');
            if (show.special_show_ticket_type === BookingType.VIP) {
                form.setFieldValue('ticketType', BookingType.VIP);
            }
            if (show.special_show_ticket_type === BookingType.STANDARD) {
                form.setFieldValue('ticketType', BookingType.STANDARD);
            }
            setSelectedShow(show);
        }
    }, [form.values.date, availability]);

    useEffect(() => {
        // Enforce "2 adults minimum OR 1 adult + 1 child" rule, but only when availability
        // allows it. If only 1 adult seat remains we skip the bump to avoid an infinite loop
        // with the adultTicketMax clamping effect below.
        if (form.values.adultTicketCount === 1 && form.values.childTicketCount === 0 && adultTicketMax >= 2) {
            form.setFieldValue('adultTicketCount', 2);
        }
    }, [form.values.ticketType, form.values.adultTicketCount, form.values.childTicketCount, adultTicketMax]);

    useEffect(() => {
        if (form.values.adultTicketCount > adultTicketMax) {
            form.setFieldValue('adultTicketCount', adultTicketMax);
        }
    }, [adultTicketMax, form.values.adultTicketCount]);

    useEffect(() => {
        if (!!redirectUrl && activePaymentService === ActivePaymentService.VIVA.toString()) {
            window.location.replace(redirectUrl);
        }
    }, [redirectUrl]);

    useEffect(() => {
        if (!!formData && activePaymentService === ActivePaymentService.CARDLINK.toString()) {
            const form = document.createElement('form');
            form.action = formData.formAction;
            form.method = 'POST';
            form.style.display = 'none';
            document.body.appendChild(form);
            Object.entries(formData.formFields).forEach(([key, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value;
                form.appendChild(input);
            });
            form.submit();
            document.body.removeChild(form);
        }
    }, [formData]);

    const convertTo12Hour = (time24: string) => {
        const [hours, minutes] = time24.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${hour12}:${minutes} ${ampm}`;
    };

    const { filteredCalendarActivatedSeasons, initialSeason, initialSeasonMonth } = useMemo(() => {
        const filteredSeasons = calendarActivatedSeasons.filter(season => season.status === 'enabled').map((season) => ({ label: season.year.toString(), value: season.year.toString() }));
        const initialSeason = filteredSeasons?.sort((a, b) => Number(a.value) - Number(b.value))?.[0]?.value || new Date().getFullYear().toString();
        const enabledMonths = [...calendarActivatedSeasons?.find(season => season.year.toString() === initialSeason.toString())?.monthsEnabled || []];
        const initialSeasonMonth = enabledMonths?.sort((a, b) => Number(a) - Number(b))?.[0] || 5;

        return { filteredCalendarActivatedSeasons: filteredSeasons, initialSeason, initialSeasonMonth };
    }, [calendarActivatedSeasons]);


    // useEffect to set the initial date of the calendar based on the initial season and month
    // if the initial season is not the current year, set the date to the first day of the initial season month
    // if the initial season is the current year, and the current month is before the initial season month, set the date to the first day of the initial season month
    // if the initial season is the current year, and the current month is after the initial season month, set the date to the current date
    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        if (initialSeason !== currentYear.toString()) {
            dispatch(getAvailability({ month: initialSeasonMonth, year: initialSeason } as any));
            const dateToApply = DateTime.fromObject({
                year: Number(initialSeason),
                month: initialSeasonMonth,
                day: 1
            }).toJSDate();
            form.setFieldValue('date', getAthensDate(dateToApply) as Date);
        } else if (calendarActivatedSeasons.length > 0 && currentMonth < initialSeasonMonth) {
            dispatch(getAvailability({ month: initialSeasonMonth, year: currentYear } as any));
            const dateToApply = DateTime.fromObject({
                year: currentYear,
                month: initialSeasonMonth,
                day: 1
            }).toJSDate();
            form.setFieldValue('date', getAthensDate(dateToApply) as Date);
        } else {
            dispatch(getAvailability({ month: currentMonth, year: currentYear } as any));
            form.setFieldValue('date', getAthensDate() as Date);
        }
    }, [initialSeason, initialSeasonMonth, calendarActivatedSeasons]);

    const cruiseBoxInfo = useMemo(() => <Box 
        mt={10} 
        className="cruise-info-box"
    >
        <Text style={{ 
            color: 'black', 
            fontSize: 'clamp(1rem, 1.2rem, 1.35rem)', 
            fontWeight: 700, 
            fontFamily: "Playfair Display", 
            transform: 'scaleY(1.1)', 
            letterSpacing: '0.05em',
            marginTop: 2,
            marginRight: -5,
        }}>
            {reservation.cruiseShipPassengersNote}
        </Text>
        <Button
            variant="unstyled"
            onClick={() => openCruiseShipPassengersModal()}
            style={{ 
                color: colors.appPink, 
                fontSize: 'clamp(1rem, 1.2rem, 1.35rem)', 
                fontWeight: 700, 
                fontFamily: "Playfair Display", 
                transform: 'scaleY(1.1)', 
                letterSpacing: '0.05em', 
                backgroundColor: 'transparent',
                cursor: 'pointer'
            }}>
            {reservation.cruiseShipPassengersClickHere}
        </Button>
    </Box>, [reservation.cruiseShipPassengersNote, reservation.cruiseShipPassengersClickHere, openCruiseShipPassengersModal]);

    return <>
        {/* Reservation Section */}
        <Grid mt={isTablet ? 20 : 50} mx={isTablet ? '2%' : '0px'} justify="center" gutter={0}>
            <Grid.Col span={{ base: 12, md: 8 }} px="xl" mb={isTablet ? 20 : 10} >
                {!isMaxLargeTablet && cruiseBoxInfo}
                <Title size={36} style={{ color: colors.appYellow, fontFamily: "Playfair Display", transform: 'scaleY(1.2)', letterSpacing: '0.1em', fontSize: 'clamp(22px, 32px, 36px)' }}>
                    {reservation.title}
                </Title>
                <Text style={{ color: colors.appYellow, fontSize: 'clamp(1rem, 1.35rem, 1.55rem)', fontFamily: "Playfair Display", transform: 'scaleY(1.1)', letterSpacing: '0.05em' }}>
                    {reservation.description}
                </Text>
                {isMaxLargeTablet && cruiseBoxInfo}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 8 }} ref={calendarRef}>
                <form onSubmit={form.onSubmit(handleBooking)}>
                    <Text style={{ fontSize: isTablet ? 'clamp(0.5rem, 0.9rem, 1.1rem)' : '1.1rem', color: 'white' }}>
                        {reservation.instructions}
                    </Text>
                    <Box className="box-container" pos="relative">
                        <LoadingOverlay
                            visible={loading}
                            zIndex={1000}
                            overlayProps={{ radius: "md", blur: 1 }}
                            loaderProps={{
                                color: colors.appPink,
                                type: 'dots',
                                size: 'sm'
                            }}
                            styles={{
                                overlay: {
                                    backgroundColor: 'rgba(255, 20, 147, 0.2)',
                                }
                            }}
                        />
                        <LoadingOverlay
                            visible={maintainanceMode}
                            zIndex={1000}
                            overlayProps={{ radius: "md", blur: 3 }}
                            loaderProps={{
                                color: colors.appPink,
                                type: 'dots',
                                size: 'sm',
                                children: <Text style={{ color: colors.appYellow, fontSize: '1.25rem', fontWeight: 'bold' }}>The website is under maintenance. Please try again later.</Text>
                            }}
                            styles={{
                                overlay: {
                                    backgroundColor: 'rgba(255, 20, 147, 0.2)',
                                }
                            }}
                        />
                        <Stack gap="md" className="calendar-container" align="center">
                            <Group>
                                <Text style={{ color: colors.appYellow, fontSize: '1.25rem', fontWeight: 'bold' }}>Season</Text>
                                {filteredCalendarActivatedSeasons.length > 1 && <Select
                                    size="lg"
                                    variant="unstyled"
                                    radius={0}
                                    styles={selectStyles}
                                    data={filteredCalendarActivatedSeasons}
                                    disabled={filteredCalendarActivatedSeasons.length === 1}
                                    value={initialSeason}
                                    onChange={(value) => {
                                        // if its current year, select the current date athens, if not the first date of the calendar season year
                                        const athensDate = getAthensDate(null, false) as DateTime;
                                        if (value === athensDate.year.toString()) {
                                            dispatch(getAvailability({ month: athensDate.month, year: athensDate.year } as any));
                                            form.setFieldValue('date', athensDate.toJSDate());
                                        } else {
                                            const season = calendarActivatedSeasons.find(season => season.year === Number(value));
                                            if (season) {
                                                dispatch(getAvailability({ month: season.monthsEnabled[0], year: season.year } as any));
                                                const firstMonth = season.monthsEnabled[0];
                                                const dateToApply = DateTime.fromObject({
                                                    year: season.year,
                                                    month: firstMonth,
                                                    day: 1
                                                }).toJSDate();
                                                form.setFieldValue('date', getAthensDate(dateToApply) as Date);
                                            }
                                        }
                                    }}
                                />}
                                {filteredCalendarActivatedSeasons.length === 1 && <Text style={{ color: colors.appYellow, fontSize: '1.25rem', fontWeight: 'bold' }}>{initialSeason}</Text>}
                            </Group>

                            <DatePicker
                                hideOutsideDates
                                value={form.values.date}
                                date={form.values.date || getAthensDate() as Date}
                                maxLevel="month"
                                onChange={(value) => {
                                    setDateClicked(value);
                                    form.clearFieldError('adultTicketCount');
                                    form.clearFieldError('childTicketCount');
                                    form.setFieldValue('ticketType', BookingType.STANDARD);
                                    form.setFieldValue('date', value);
                                    const show = availability?.find((day: AvailabilityDay) => areAthensAndCalendarDatesEqual(day?.date, value))?.shows?.[0];
                                    if (show) {
                                        const availableAdultSeats = show.available_adult_seats;
                                        const availableChildrenSeats = show.available_children_seats;

                                        if (availableAdultSeats < form.values.adultTicketCount) {
                                            if (!availableAdultSeats) {
                                                form.setFieldError('adultTicketCount', 'No adult tickets available');
                                            } else {
                                                form.setFieldError('adultTicketCount', `You can only book ${availableAdultSeats} adult tickets`);
                                            }
                                        }
                                        if (availableChildrenSeats < form.values.childTicketCount) {
                                            if (!availableChildrenSeats) {
                                                form.setFieldError('childTicketCount', 'No child tickets available');
                                            } else {
                                                form.setFieldError('childTicketCount', `You can only book ${availableChildrenSeats} child tickets`);
                                            }
                                        }
                                    }
                                }}
                                classNames={{
                                    day: 'custom-calendar-day',
                                    calendarHeaderControl: 'custom-calendar-header-control',
                                    calendarHeaderLevel: 'custom-calendar-header-level',
                                }}
                                defaultDate={getAthensDate() as Date}
                                minDate={minDate}
                                maxDate={maxDate}
                                onNextMonth={(nextMonthDate) => {
                                    const nextMonth = DateTime.fromJSDate(nextMonthDate);
                                    // Get availability for next month
                                    dispatch(getAvailability({
                                        month: nextMonth.month,
                                        year: nextMonth.year
                                    } as any));
                                    // Update the form date to first day of next month to update the displayed month
                                    const dateToApply = nextMonth.set({ day: 1 }).toJSDate();
                                    form.setFieldValue('date', dateToApply);
                                }}
                                onPreviousMonth={(prevMonthDate) => {
                                    const newAthensDate = getAthensDate(null, false) as DateTime;
                                    const prevMonth = DateTime.fromJSDate(prevMonthDate);
                                    dispatch(getAvailability({
                                        month: prevMonth.month,
                                        year: prevMonth.year
                                    } as any));
                                    // Update the form date to first day of previous month to update the displayed month
                                    // if the previous month is the current month, set the date to the current date
                                    const dateToApply = prevMonth.month === newAthensDate.month ? prevMonth.set({ day: newAthensDate.day }) : prevMonth.set({ day: 1 });
                                    form.setFieldValue('date', dateToApply.toJSDate());
                                }}
                                getDayProps={(date) => {
                                    const dateYear = date.getFullYear();
                                    const dateMonth = date.getMonth() + 1;
                                    const activeSeason = calendarActivatedSeasons.find(
                                        s => s.status === 'enabled' && s.year === dateYear
                                    );
                                    const isInActivatedCalendar = activeSeason?.monthsEnabled.includes(dateMonth) ?? false;

                                    if (!isInActivatedCalendar) {
                                        return { disabled: true };
                                    }

                                    const availability = getDateAvailability(date);
                                    if (!availability) return {};

                                    const colors = {
                                        full: { backgroundColor: fullShowColor, cursor: 'not-allowed' },
                                        almostFull: { backgroundColor: almostFullShowColor },
                                        low: { backgroundColor: availableShowColor }
                                    };

                                    return {
                                        style: {
                                            ...colors[availability],
                                            color: 'white',
                                            ...(availability === ShowAvailabilityStatus.FULL && { pointerEvents: 'none' })
                                        },
                                        disabled: availability === ShowAvailabilityStatus.FULL
                                    };
                                }}
                                styles={datePickerStyles}
                            />

                            {isPaymentCustomerDetailsEnabled && (
                                <>
                                    <Group grow w="100%">
                                        <TextInput
                                            radius={0}
                                            label={reservation.form.email.label}
                                            placeholder={reservation.form.email.placeholder}
                                            required
                                            value={form.values.email}
                                            onChange={(event) => form.setFieldValue('email', event.currentTarget.value?.trim())}
                                            error={form.errors.email}
                                            styles={getInputStyles(form.errors.email) as any}
                                            variant="unstyled"
                                            classNames={{ root: 'calendar-input' }}
                                        />
                                        <TextInput
                                            radius={0}
                                            label="Confirm Email"
                                            placeholder="Confirm your email"
                                            required
                                            value={form.values.confirmEmail}
                                            onChange={(event) => form.setFieldValue('confirmEmail', event.currentTarget.value?.trim())}
                                            error={form.errors.confirmEmail}
                                            styles={getInputStyles(form.errors.confirmEmail) as any}
                                            variant="unstyled"
                                            classNames={{ root: 'calendar-input' }}
                                        />
                                    </Group>
                                    <Group grow w="100%">
                                        <TextInput
                                            radius={0}
                                            label={reservation.form.fullName.label}
                                            placeholder={reservation.form.fullName.placeholder}
                                            required
                                            value={form.values.fullName}
                                            onChange={(event) => form.setFieldValue('fullName', event.currentTarget.value)}
                                            error={form.errors.fullName}
                                            styles={getInputStyles(form.errors.fullName) as any}
                                            variant="unstyled"
                                            classNames={{ root: 'calendar-input' }}
                                        />
                                        <TextInput
                                            radius={0}
                                            label={reservation.form.phoneNumber.label}
                                            placeholder={reservation.form.phoneNumber.placeholder}
                                            required
                                            value={form.values.phoneNumber}
                                            onChange={(event) => form.setFieldValue('phoneNumber', event.currentTarget.value)}
                                            error={form.errors.phoneNumber}
                                            styles={getInputStyles(form.errors.phoneNumber) as any}
                                            variant="unstyled"
                                            classNames={{ root: 'calendar-input' }}
                                        />
                                    </Group>
                                </>
                            )}
                            <Group grow w="100%">
                                <Textarea
                                    radius={0}
                                    label={reservation.form.notes.label}
                                    placeholder={reservation.form.notes.placeholder}
                                    value={form.values.notes}
                                    onChange={(event) => form.setFieldValue('notes', event.currentTarget.value)}
                                    error={form.errors.notes}
                                    styles={getTextareaStyles(form.errors.notes) as any}
                                />
                            </Group>
                            <Group w="100%">
                                <Text style={{ fontSize: '0.85rem', color: 'white' }}>
                                    {reservation.form.travelInsurancePromo}
                                <Button
                                    variant="unstyled"
                                    onClick={openInsuranceModal}
                                    style={{
                                        color: colors.appPink,
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        backgroundColor: 'transparent',
                                        cursor: 'pointer',
                                        padding: 0,
                                        height: 'auto',
                                        minHeight: 'unset',
                                    }}>
                                    {reservation.form.travelInsuranceClickHere}
                                </Button>
                                </Text>
                            </Group>
                        </Stack>
                        <Stack gap="md" align="center">
                            {!selectedShow?.special_show_ticket_type && <SegmentedControl
                                value={form.values.ticketType}
                                onChange={(value) => form.setFieldValue('ticketType', value)}
                                color={colors.appPink}
                                data={[
                                    { label: reservation.form.ticketTypes.standard, value: BookingType.STANDARD },
                                    { label: reservation.form.ticketTypes.golden, value: BookingType.VIP },
                                ]}
                                style={{ backgroundColor: colors.appYellow, padding: '10px', borderRadius: '10px' }}
                                transitionDuration={250}
                                transitionTimingFunction="linear"
                            />}

                            <NumberInput
                                value={form.values.adultTicketCount}
                                onChange={(value) => form.setFieldValue('adultTicketCount', Number(value))}
                                min={form.values.childTicketCount > 0 ? 1 : Math.min(2, adultTicketMax)}
                                max={adultTicketMax}
                                label={`${reservation.form.adultTicket.label} ${form.values.ticketType === BookingType.STANDARD ? `${standardTicketPrice}€` : `${vipTicketPrice}€`}`}
                                clampBehavior="strict"
                                style={{ width: '150px' }}
                                error={form.errors.adultTicketCount}
                                styles={numberAdultInputStyles as any}
                                description={reservation.form.adultTicket.description}
                                allowNegative={false}
                                allowDecimal={false}
                            />
                            <NumberInput
                                value={form.values.childTicketCount}
                                onChange={(value) => form.setFieldValue('childTicketCount', Number(value))}
                                min={0}
                                max={childTicketMax}
                                label={`${reservation.form.childTicket.label} ${form.values.ticketType === BookingType.STANDARD ? `${childTicketPrice}€` : `${vipTicketChildPrice}€`}`}
                                clampBehavior="strict"
                                style={{ width: '150px' }}
                                error={form.errors.childTicketCount}
                                styles={numberInputStyles as any}
                                description={reservation.form.childTicket.description}
                                allowNegative={false}
                                allowDecimal={false}
                            />
                            <Stack gap={0} align="flex-start" w="100%" px="md" style={{ maxWidth: 300 }}>
                                <Group gap="xs" w="100%" align="flex-start" justify="center" wrap='nowrap'>
                                    <TextInput
                                        radius={0}
                                        label="Promo code"
                                        placeholder="Enter promo code"
                                        value={promoCodeInput}
                                        onChange={(event) => setPromoCodeInput(event.currentTarget.value)}
                                        disabled={promoCodeValidation?.valid}
                                        styles={getInputStyles(undefined) as any}
                                        variant="unstyled"
                                        classNames={{ root: 'calendar-input' }}
                                        style={{ flex: 1 }}
                                    />
                                    {promoCodeValidation?.valid ? (
                                        <Button
                                            radius="xl"
                                            variant="outline"
                                            style={{ color: colors.appYellow, borderColor: colors.appYellow, marginLeft: -100, height: '30px', marginTop: 10 }}
                                            onClick={() => {
                                                dispatch(clearPromoCode());
                                                setPromoCodeInput('');
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    ) : (
                                        <Button
                                            radius="xl"
                                            loading={promoCodeLoading}
                                            disabled={!promoCodeInput.trim()}
                                            style={{ backgroundColor: colors.appPink, marginLeft: -90, height: '30px', marginTop: 10 }}
                                            onClick={handleApplyPromoCode}
                                        >
                                            Apply
                                        </Button>
                                    )}
                                </Group>
                                {promoCodeValidation && (
                                    <Text style={{ fontSize: '0.85rem', color: promoCodeValidation.valid ? availableShowColor : fullShowColor, marginTop: -20 }}>
                                        {promoCodeValidation.valid
                                            ? `Promo code applied — ${promoDiscountLabel} off!`
                                            : (promoCodeValidation.message || 'Invalid promo code')}
                                    </Text>
                                )}
                            </Stack>
                            {promoDiscountAmount > 0 ? (
                                <Text style={textStyles}>
                                    <span style={{ textDecoration: 'line-through', opacity: 0.7, marginRight: 8 }}>
                                        {totalPrice.toFixed(2)}€
                                    </span>
                                    {reservation.form.total.replace('{totalPrice}', discountedTotalPrice.toFixed(2))}
                                </Text>
                            ) : (
                                <Text style={textStyles}>
                                    {reservation.form.total.replace('{totalPrice}', totalPrice.toFixed(2))}
                                </Text>
                            )}
                            <Text style={textStyles}>
                                {form.values.date
                                    ? `${reservation.form.date.label} ${(fromCalendarDateToAthens(form.values.date, false) as DateTime).toFormat('MMMM d, yyyy')}`
                                    : reservation.form.date.placeholder
                                }<br />
                                {form.values.showTime && `${reservation.form.showTime} ${form.values.showTime} (${convertTo12Hour(form.values.showTime)})`}
                                {form.values.date && isSelectedShowAlmostFull && <Text style={{
                                    fontSize: '1rem', color: almostFullShowColor
                                }}>
                                    {reservation.form.almostFull}
                                </Text>}
                            </Text>

                            {showTransportation && (
                                <Stack gap="xs" w="100%" px="md" style={{ maxWidth: 320 }}>
                                    <Checkbox
                                        checked={form.values.includeTransportation}
                                        onChange={(event) => {
                                            const checked = event.currentTarget.checked;
                                            form.setFieldValue('includeTransportation', checked);
                                            if (!checked) {
                                                form.setFieldValue('transportationDestination', null);
                                                form.clearFieldError('transportationDestination');
                                            }
                                        }}
                                        label={transportationCheckboxLabel}
                                        color={colors.appPink}
                                        styles={checkBoxStyles}
                                    />
                                    <Select
                                        radius={0}
                                        description={(reservation.form as any).transportationPlaceholder || 'Select your ride back to the main square of:'}
                                        placeholder='Select your destination'
                                        data={transportationOptions}
                                        value={form.values.transportationDestination}
                                        onChange={(value) => form.setFieldValue('transportationDestination', value)}
                                        disabled={!form.values.includeTransportation}
                                        error={form.errors.transportationDestination}
                                        variant="unstyled"
                                        styles={transportationSelectStyles as any}
                                    />
                                </Stack>
                            )}

                            <Checkbox
                                checked={form.values.hasTravelInsurance}
                                onChange={(event) => form.setFieldValue('hasTravelInsurance', event.currentTarget.checked)}
                                label={`${reservation.form.travelInsuranceCheckbox.replace('5€', `${travelInsurancePrice}€`)}`}
                                color={colors.appPink}
                                styles={checkBoxStyles}
                            />

                            <Checkbox
                                checked={form.values.acceptTerms}
                                onChange={(event) => form.setFieldValue('acceptTerms', event.currentTarget.checked)}
                                label={reservation.form.cancellationCheckbox}
                                error={form.errors.acceptTerms}
                                color={colors.appPink}
                                styles={checkBoxStyles}
                            />

                            <Button
                                disabled={!form.isValid() || Object.keys(form.errors).length > 0 || !canPay}
                                loading={bookingCreateLoading}
                                rightSection={activePaymentService === ActivePaymentService.VIVA.toString() && !loading ? <Image src={vivaLogo} alt="viva" height={10} style={{ maxWidth: '60px' }} /> : null}
                                size="sm"
                                radius="xl"
                                style={{ backgroundColor: colors.appPink, '&:hover': { backgroundColor: colors.appLightestBlue }, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.5)' }}
                                type="submit">
                                {reservation.form.payNow}
                            </Button>
                            <Text style={{ fontSize: '0.70rem', color: 'white', fontWeight: 'bold' }}>
                                {reservation.form.paymentNote}
                                <br />
                                {reservation.form.additionalFees.replace('{additionalFeePercentage}', additionalFeePercentage.toFixed(2))}
                                <br />
                                <br />
                                {reservation.form.cancelationPolicy}
                                <br />
                                {reservation.form.dateChangesHeader}
                                {reservation.form.dateChangesDescription}
                                <br />
                                {reservation.form.dateChangesNoteStandard}
                                <br />
                                {reservation.form.dateChangesNoteGolden}
                            </Text>
                        </Stack>
                    </Box>
                </form>
            </Grid.Col>
            <DialogStatus
                dialogProps={{
                    position: { left: '37%', bottom: 20 },
                    styles: {
                        root: {
                            backgroundColor: colors.appPink,
                        },
                        closeButton: {
                            color: colors.appWhite,
                        },
                    }
                }}
                textProps={{
                    style: {
                        fontSize: '1rem',
                        fontFamily: "Helvetica",
                        textAlign: 'center',
                        marginRight: '10px'
                    }
                }}
                showButton={false}
                errorColor={colors.appWhite}
                successColor={colors.appYellow}
            />
        </Grid>

        {/* Cruise Ship Passengers Tour Modal */}
        <CruiseShipModal
            opened={openedCruiseShipPassengersModal}
            onClose={closeCruiseShipPassengersModal}
        />

        {/* Travel Insurance Info Modal */}
        <Modal
            opened={openedInsuranceModal}
            onClose={closeInsuranceModal}
            title={reservation.form.travelInsuranceModal.title}
            size="md"
            radius="md"
            withCloseButton
            zIndex={1000}
            styles={{
                content: {
                    backgroundColor: colors.appLightBlue,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                },
                header: {
                    backgroundColor: colors.appLightBlue,
                },
                body: {
                    padding: 20,
                    color: 'white',
                },
                title: {
                    color: colors.appYellow,
                    fontSize: 'clamp(1.2rem, 2.4vw, 1.6rem)',
                    fontWeight: 700,
                    fontFamily: "Playfair Display",
                },
            }}>
            <Stack gap="md">
                <Text fw={700} style={{ fontSize: 'clamp(0.85rem, 2vw, 1rem)' }}>
                    {reservation.form.travelInsuranceModal.line1}
                </Text>
                <Text style={{ fontSize: 'clamp(0.8rem, 2vw, 0.95rem)' }}>
                    {reservation.form.travelInsuranceModal.line2}
                </Text>
                <Stack gap="xs">
                    {reservation.form.travelInsuranceModal.benefits.map((benefit, index) => (
                        <Text key={index} style={{ fontSize: 'clamp(0.8rem, 2vw, 0.95rem)' }}>
                            ✅ {benefit}
                        </Text>
                    ))}
                </Stack>
                <Text fw={700} style={{ fontSize: 'clamp(0.85rem, 2vw, 1rem)', color: colors.appYellow }}>
                    {reservation.form.travelInsuranceModal.closing.replace('5€', `${travelInsurancePrice}€`)}
                </Text>
            </Stack>
        </Modal>
    </>
};

export default ReservationPage;